import { getTimelineDate } from "./helpers/route-start";
import { useLiveTimelineDate } from "../../../hooks/use-live-timeline-date";
import { FC, useMemo, useRef, useState, useEffect, useCallback } from "react";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import { DateTime } from "luxon";
import { toast } from "react-toastify";

import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { OpenInFull, CloseFullscreen, InfoOutlined } from "@mui/icons-material";
import { Nullable } from "service";
import {
  AdminGetRoutesResDto,
  AdminGetDateRouteNotificationsResDto,
} from "service/api/estabrookSchemas";
import { SkippedDirectionArrow } from "ui/icons";
import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import {
  formatNumberToMinutes,
  formatNumberToMinutesPrecise,
  formatMinutesToHoursAndMinutes,
  paletteColorStringRenderer,
} from "../../../utils/string-helpers";
import OrderLayout from "./dnd/order-layout";
import HoveredAddress from "../region-management/hovered-address/hovered-address";
import useRoutePlannerStore from "../../../state/route-planner-store";
import {
  Active,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  KeyboardSensor,
  Over,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
  CollisionDetection,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { generateHourOptions, getMinutesOfDay } from "../../../utils/date-time";
import { RouteStatus } from "./helpers/route-status.enum";
import { stopServiceMinutes } from "./helpers/stop-service-minutes";
import { getLiveTimelineLocations } from "./helpers/live-timeline-locations";
import {
  compareWithOriginalEstimate,
  EstimateComparison,
} from "./helpers/estimate-status";
import {
  getRouteEstimatedTimeMinutes,
  getRouteStopTimings,
} from "./helpers/route-estimate";
import { useRoutePlannerDragState } from "../../../hooks/use-route-planner-drag-state";
import {
  generateHourlySlots,
  generateZoomAwareHourlySlots,
  getMinAndMaxTime,
  TIMETABLE_ITEM_WIDTH,
} from "./helpers/timetable";
import { findContainer } from "./helpers";
import CurrentTimeIndicator from "./live/current-time-indicator";
import DriverControlledSelect from "./driver-controlled-select";
import useRoutePlannerApi from "../../../hooks/use-route-planner-api";
import { useParams } from "react-router-dom";
import useHoverAddressDetails from "../../../hooks/use-hover-address-details";
import { ClickedAddress } from "../../../shared/hooks/use-address-click-handler";
import DropPositionTooltip from "../components/generic-table/drop-position-tooltip";
import { useDragTooltip } from "../../../shared/hooks/useDragTooltip";
import { Modals } from "../../../state/route-planner-store";
import { NotificationBell } from "../../../features/route-planning/components/notification-bell";
import TimetableZoomControls from "./components/timetable-zoom-controls";
import useTimetableZoomStore from "../../../state/timetable-zoom-store";
import { useRoutePlannerAdjustments } from "../../../hooks/use-route-planner-adjustments";

export interface RoutePlannerTableProps {
  data: AdminGetRoutesResDto[];
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
  onRouteClick: (value: string) => void;
  onClickedAddress: (value: ClickedAddress) => void;
  onSelectRouteDriver: (
    routeId: string,
    routeName: string,
    startTime: string,
    driverId: string,
    driverName: string,
    currentDriverRoutes?: AdminGetRoutesResDto[],
  ) => void;
  onSelectStartTime: (
    routeId: string,
    startTime: string,
    availableForAnyDriver: boolean,
    driverId?: string,
  ) => void;
  refetchData?: () => void;
  handleAddVirtualRoute?: (index: number) => void;
  handleDeleteVirtualRoute: (routeId: string, routeName: string) => void;
  isLoading: boolean;
  currentRouteStatus?: RouteStatus;
  queryParamDate?: string;
  openModal?: (modal: Modals) => void;
  globalNotificationSettings?: AdminGetDateRouteNotificationsResDto; // Global notification settings from API
  onRealtimeAdjustmentsChange?: (
    adjustedServiceTimeMap: Record<string, number>,
    adjustedEstimatedTimeMinutesMap: Record<string, number>,
  ) => void;
}

const describeEstimateComparison = ({
  status,
  differenceMinutes,
}: EstimateComparison): string => {
  const difference = formatNumberToMinutes(Math.abs(differenceMinutes));
  if (status === "behind") return `${difference} behind the original estimate`;
  if (status === "ahead") return `${difference} ahead of the original estimate`;
  return differenceMinutes === 0
    ? "On schedule"
    : `On schedule (${difference} ${differenceMinutes > 0 ? "behind" : "ahead"}, within 20 min)`;
};

const RoutePlannerTable: FC<RoutePlannerTableProps> = (props) => {
  const {
    data = [],
    sortOptions,
    sortOptionsChange,
    isLoading,
    onRouteClick,
    onSelectRouteDriver,
    onSelectStartTime,
    onClickedAddress,
    currentRouteStatus,
    handleAddVirtualRoute,
    handleDeleteVirtualRoute,
    refetchData,
    openModal,
    globalNotificationSettings,
    onRealtimeAdjustmentsChange,
  } = props;
  const { date } = useParams();
  const { driverWsStatus, updateRoutePriority, driversData, isLoadingDrivers } =
    useRoutePlannerApi({ date: date as string });
  const { routes, setRoutes, setPendingNotificationUpdate } =
    useRoutePlannerStore();

  const { palette } = useTheme();
  const { findAddress, minAndMaxStartTimeOfRoutes } = useRoutePlannerStore();
  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    setViewportWidth,
    setZoomLevel,
    setActualTimelineHours,
  } = useTimetableZoomStore();
  const styles = useStyles();
  const [isDragging, setIsDragging] = useState(false);
  const fadeRef = useRef<HTMLDivElement>(null);
  const lastOverIdRef = useRef<string | null>(null);
  const dragPointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const dragAutoScrollFrameRef = useRef<number | null>(null);

  // Skipped column collapse state
  const [isSkippedCollapsed, setIsSkippedCollapsed] = useState(true);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  // Whether the table has real horizontal overflow — drives whether the
  // skipped-column expand button is shown (no point expanding if everything fits).
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  // Collapsed: fixed, compact width (clipped so it never overlaps neighboring
  // columns). Expanded: grows with the number of skipped orders in the
  // busiest route, so the column can fit them all without overlapping the
  // Time/Addresses area next to it (ESR-1615 QA follow-up:
  // https://estabrook.atlassian.net/browse/ESR-1615).
  const SKIPPED_COLUMN_BASE_MIN_WIDTH = 150;

  const maxSkippedCount = useMemo(
    () =>
      data.reduce((max, route) => {
        const skippedCount =
          route.locations?.filter((loc) => loc.order?.status === "skipped")
            .length ?? 0;
        return Math.max(max, skippedCount);
      }, 0),
    [data],
  );

  const expandedSkippedColumnWidthPx = useMemo(() => {
    const SKIPPED_ITEM_WIDTH = 11; // fixed width of a single skipped-order chip
    const SKIPPED_ITEM_GAP = 4; // spacing between chips
    const SKIPPED_ICON_WIDTH = 20; // SkippedDirectionArrow icon
    const SKIPPED_ICON_GAP = 8; // gap between the icon and the chips
    // Left cell padding only — paddingRight is explicitly zeroed for this
    // column (routePlannerTableStyle, "& tbody th:last-of-type").
    const SKIPPED_CELL_PADDING_LEFT = 24;

    if (maxSkippedCount === 0) {
      return SKIPPED_COLUMN_BASE_MIN_WIDTH;
    }

    const itemsWidth =
      maxSkippedCount * SKIPPED_ITEM_WIDTH +
      (maxSkippedCount - 1) * SKIPPED_ITEM_GAP;
    const contentWidth = SKIPPED_ICON_WIDTH + SKIPPED_ICON_GAP + itemsWidth;

    return Math.max(
      SKIPPED_COLUMN_BASE_MIN_WIDTH,
      contentWidth + SKIPPED_CELL_PADDING_LEFT,
    );
  }, [maxSkippedCount]);

  // DOM node per route for the real "Skipped" table cell. Content always
  // portals here instead of faking sticky from the Addresses cell (which
  // detaches once you scroll past that cell's edge).
  const [skippedCellTargets, setSkippedCellTargets] = useState<
    Record<string, HTMLDivElement | null>
  >({});

  const registerSkippedCellTarget = useCallback(
    (routeId: string, node: HTMLDivElement | null) => {
      setSkippedCellTargets((prev) =>
        prev[routeId] === node ? prev : { ...prev, [routeId]: node },
      );
    },
    [],
  );

  // Stable ref callback per route — avoids infinite re-render loop caused by
  // inline callbacks creating new identities on every render.
  const skippedCellRefCallbacks = useRef<
    Map<string, (node: HTMLDivElement | null) => void>
  >(new Map());

  const getSkippedCellRefCallback = useCallback(
    (routeId: string) => {
      let callback = skippedCellRefCallbacks.current.get(routeId);
      if (!callback) {
        callback = (node: HTMLDivElement | null) =>
          registerSkippedCellTarget(routeId, node);
        skippedCellRefCallbacks.current.set(routeId, callback);
      }
      return callback;
    },
    [registerSkippedCellTarget],
  );

  // Column visibility state for draggable divider
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [dividerPosition, setDividerPosition] = useState<number | null>(null);
  const [dragDividerPosition, setDragDividerPosition] = useState<number | null>(
    null,
  );
  const tableRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    isDragging: boolean;
    rafId: number | null;
    lastHiddenColumns: Set<string>;
    columnBoundaries: Map<string, { left: number; right: number }>;
    previousHiddenColumns: Set<string>;
    startX: number | null;
    isInitializing: boolean;
  }>({
    isDragging: false,
    rafId: null,
    lastHiddenColumns: new Set(),
    columnBoundaries: new Map(),
    previousHiddenColumns: new Set(),
    startX: null,
    isInitializing: false,
  });

  const { changeOrderStatus, moveOrderOnDragOver, routesBeforeDrag } =
    useRoutePlannerDragState({
      currentRouteStatus,
      refetchData,
      routes: data,
    });

  const getTableScrollContainer = useCallback(() => {
    if (!tableRef.current) {
      return null;
    }

    return tableRef.current.querySelector(
      ".MuiTableContainer-root",
    ) as HTMLElement | null;
  }, []);

  // Keep hasHorizontalOverflow accurate before any scroll event fires
  // (initial load, resize, zoom/data changes).
  useEffect(() => {
    const scrollContainer = getTableScrollContainer();
    if (!scrollContainer) {
      return;
    }

    const checkOverflow = () => {
      setHasHorizontalOverflow(
        scrollContainer.scrollWidth > scrollContainer.clientWidth + 10,
      );
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(scrollContainer);

    return () => resizeObserver.disconnect();
  }, [getTableScrollContainer, data, zoomLevel]);

  const updateDragPointerPosition = useCallback((x: number, y: number) => {
    dragPointerPositionRef.current = { x, y };
  }, []);

  // Initialize zoom to show 24 hours by default (1.0 = 100%)
  useEffect(() => {
    // Set a reasonable viewport width (will be updated by actual measurements)
    setViewportWidth(1200);

    // Start with zoom level 1.0 which shows 24 hours
    setZoomLevel(1.0);
  }, [setViewportWidth, setZoomLevel]);

  // Update actual timeline hours when data changes to set minimum zoom
  useEffect(() => {
    const realRoutesData = data.filter(
      (route) => route.id !== "unassigned-route",
    );

    if (realRoutesData.length > 0) {
      // Get the actual timeline hours using the original function
      const actualTimelineSlots = generateHourlySlots(realRoutesData);
      // Use actual hours (don't force minimum 24)
      const actualHours = actualTimelineSlots.length;
      setActualTimelineHours(actualHours);
      // The store will automatically calculate minimum zoom as 24 / actualHours
    } else {
      // Default to 24 hours if no routes
      setActualTimelineHours(24);
    }
  }, [data, setActualTimelineHours]);

  // Handle mouse wheel zoom with Ctrl key - only when over timetable
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      // Check if the event target is within the timetable by looking for table elements
      const target = e.target as Element;
      const isWithinTable =
        target.closest("table") ||
        target.closest('[data-testid*="table"]') ||
        target.closest(".MuiTableContainer-root");

      if (!isWithinTable) {
        return;
      }

      // Only zoom when Ctrl key is pressed
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        if (e.deltaY < 0) {
          // Zoom in
          zoomIn();
        } else {
          // Zoom out
          zoomOut();
        }
      }
    },
    [zoomIn, zoomOut],
  );

  // Handle keyboard shortcuts - only when focused on table elements
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Check if the currently focused element is within the table
      const activeElement = document.activeElement as Element;
      const isWithinTable =
        activeElement?.closest("table") ||
        activeElement?.closest('[data-testid*="table"]') ||
        activeElement?.closest(".MuiTableContainer-root") ||
        activeElement?.closest('[role="table"]');

      if (!isWithinTable) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "+":
          case "=":
            e.preventDefault();
            zoomIn();
            break;
          case "-":
            e.preventDefault();
            zoomOut();
            break;
          case "0":
            e.preventDefault();
            // Reset to default zoom (24 hours)
            setZoomLevel(1.0);
            break;
        }
      }
    },
    [zoomIn, zoomOut, setZoomLevel],
  );

  // Add event listeners
  useEffect(() => {
    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleWheel, handleKeyDown]);

  // Handle ESC key to cancel drag
  useEffect(() => {
    if (!isDragging) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleDragCancel();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) {
      dragPointerPositionRef.current = null;
      if (dragAutoScrollFrameRef.current !== null) {
        cancelAnimationFrame(dragAutoScrollFrameRef.current);
        dragAutoScrollFrameRef.current = null;
      }
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      updateDragPointerPosition(event.clientX, event.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }

      updateDragPointerPosition(touch.clientX, touch.clientY);
    };

    const autoScrollWhileDragging = () => {
      const scrollContainer = getTableScrollContainer();
      const pointerPosition = dragPointerPositionRef.current;

      if (scrollContainer && pointerPosition) {
        const rect = scrollContainer.getBoundingClientRect();
        const verticalThreshold = Math.min(140, rect.height * 0.2);
        let nextScrollTop = scrollContainer.scrollTop;

        if (pointerPosition.y < rect.top + verticalThreshold) {
          const intensity =
            1 - Math.max(pointerPosition.y - rect.top, 0) / verticalThreshold;
          nextScrollTop -= Math.ceil(intensity * 24);
        } else if (pointerPosition.y > rect.bottom - verticalThreshold) {
          const intensity =
            1 -
            Math.max(rect.bottom - pointerPosition.y, 0) / verticalThreshold;
          nextScrollTop += Math.ceil(intensity * 24);
        }

        if (nextScrollTop !== scrollContainer.scrollTop) {
          scrollContainer.scrollTop = nextScrollTop;
        }
      }

      dragAutoScrollFrameRef.current = requestAnimationFrame(
        autoScrollWhileDragging,
      );
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    window.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    dragAutoScrollFrameRef.current = requestAnimationFrame(
      autoScrollWhileDragging,
    );

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("touchmove", handleTouchMove);

      if (dragAutoScrollFrameRef.current !== null) {
        cancelAnimationFrame(dragAutoScrollFrameRef.current);
        dragAutoScrollFrameRef.current = null;
      }
    };
  }, [getTableScrollContainer, isDragging, updateDragPointerPosition]);

  // Handle scroll detection to auto-collapse skipped column at the end and detect header visibility
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;

      // Ensure target is a valid Element with closest method (not document, window, or text node)
      if (!target || typeof target.closest !== "function") {
        return;
      }

      // Check if this is a table container scroll
      if (
        target.classList?.contains("MuiTableContainer-root") ||
        target.closest(".MuiTableContainer-root")
      ) {
        const scrollContainer = target.classList.contains(
          "MuiTableContainer-root",
        )
          ? target
          : target.closest(".MuiTableContainer-root");

        if (scrollContainer) {
          const { scrollLeft, scrollWidth, clientWidth, scrollTop } =
            scrollContainer;
          // Only treat as "scrolled to end" if there's real overflow — otherwise
          // it permanently hides the expand icon on tables that fit the viewport.
          const overflowsHorizontally = scrollWidth > clientWidth + 10;
          setHasHorizontalOverflow(overflowsHorizontally);
          const isAtEnd =
            overflowsHorizontally &&
            scrollLeft + clientWidth >= scrollWidth - 10; // 10px threshold

          // Check if the header is scrolled out of view (first header row height is typically 70px)
          setIsHeaderHidden(scrollTop > 70);

          // Recalculate divider position on scroll
          if (tableRef.current && !dragStateRef.current.isDragging) {
            const ordersHeader = tableRef.current.querySelector(
              '[data-header-value="orders"]',
            );
            if (ordersHeader) {
              const tableRect = tableRef.current.getBoundingClientRect();
              const headerRect = ordersHeader.getBoundingClientRect();
              const newPosition = headerRect.left - tableRect.left - 4;
              setDividerPosition(newPosition);
            }
          }

          // Track if we're scrolled to the end
          setIsScrolledToEnd(isAtEnd);
          // Auto-collapse removed — isSkippedCollapsed now only changes via
          // the explicit toggle click (auto-collapse overrode manual expands).
        }
      }
    };

    // Add scroll listener to capture phase to catch all scroll events
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const {
    handleBoxMouseLeave,
    handleBoxMouseEnter,
    handleMouseLeave,
    handleMouseEnter,
    isHovering,
    hoverPosition,
    hoveredAddress,
  } = useHoverAddressDetails({ allowTooltip: !isDragging, findAddress });

  const {
    allAdjustmentsMap,
    allAdjustedEstimatedTimeMinutesMap,
    handleAdjustmentsChange,
  } = useRoutePlannerAdjustments(data);

  const syncedCurrentTime = useRoutePlannerStore(
    (state) => state.syncedCurrentTime,
  );
  const selectedTimelineDate =
    DateTime.fromFormat(props.queryParamDate ?? "", "MM-dd-yyyy").toISODate() ??
    undefined;
  const timelineDate = useLiveTimelineDate(
    data,
    syncedCurrentTime,
    selectedTimelineDate,
  );
  const rowTimelineDate = useCallback(
    (route: Pick<AdminGetRoutesResDto, "date" | "state" | "locations">) =>
      route.date?.slice(0, 10) ===
      (selectedTimelineDate ?? data[0]?.date?.slice(0, 10))
        ? timelineDate
        : getTimelineDate([route], syncedCurrentTime),
    [selectedTimelineDate, data, timelineDate, syncedCurrentTime],
  );

  const timelineLocationsMap = useMemo(
    () =>
      Object.fromEntries(
        data.map((route) => [
          route.id,
          getLiveTimelineLocations(route, rowTimelineDate(route)),
        ]),
      ),
    [data, rowTimelineDate],
  );

  // Each stop's own service minutes from the cumulative serviceTimeMin, read
  // in the order the server lists a route's stops.
  const serviceTimeMap = useMemo(() => {
    const map: Record<string, number> = {};

    data.forEach((route) => {
      if (route.id === "unassigned-route" || !route.locations) return;
      Object.assign(map, stopServiceMinutes(timelineLocationsMap[route.id]));
    });

    return map;
  }, [data, timelineLocationsMap]);

  // Calculate estimated arrival time map based on array positions
  // This recalculates ETAs from scratch because when addresses are reordered
  // (e.g., driver starts address 20 instead of address 5), the backend's
  // estimatedTimeMinutes values may not be updated until EST_TIME_CALCULATED event.
  // By calculating based on array position, we get correct ETAs immediately.
  const estimatedArrivalTimeMap = useMemo(() => {
    const map: Record<string, number> = {};

    data.forEach((route) => {
      if (route.id === "unassigned-route" || !route.locations) return;

      Object.entries(
        getRouteStopTimings(timelineLocationsMap[route.id]),
      ).forEach(([locationId, timing]) => {
        map[locationId] = timing.arrivalMinutes;
      });
    });

    return map;
  }, [data, timelineLocationsMap]);

  // Share real-time adjustment maps with the parent page so sidebar cards
  // can display the same values as the route hover tooltip.
  useEffect(() => {
    onRealtimeAdjustmentsChange?.(
      allAdjustmentsMap,
      allAdjustedEstimatedTimeMinutesMap,
    );
  }, [
    allAdjustmentsMap,
    allAdjustedEstimatedTimeMinutesMap,
    onRealtimeAdjustmentsChange,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = () => {
    setIsDragging(true);
    lastOverIdRef.current = null; // Reset on drag start
  };

  // Custom collision detection that prioritizes skipped zones for easier targeting
  const customCollisionDetection: CollisionDetection = (args) => {
    const { droppableContainers, pointerCoordinates } = args;

    if (!pointerCoordinates) {
      return rectIntersection(args);
    }

    const collisions: Array<{ id: string; data: { value: number } }> = [];

    for (const droppable of droppableContainers) {
      const { rect, id } = droppable;
      if (!rect.current) continue;

      const { top, left, bottom, right } = rect.current;
      const isSkippedZone = String(id).includes("-skipped");

      // For skipped zones, add a buffer/tolerance to make them easier to target
      const buffer = isSkippedZone ? 10 : 0;

      // Check if pointer is within the droppable area (with buffer for skipped zones)
      const isIntersecting =
        pointerCoordinates.x >= left - buffer &&
        pointerCoordinates.x <= right + buffer &&
        pointerCoordinates.y >= top - buffer &&
        pointerCoordinates.y <= bottom + buffer;

      if (isIntersecting) {
        // Calculate distance from center for sorting
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;
        const distanceX = pointerCoordinates.x - centerX;
        const distanceY = pointerCoordinates.y - centerY;
        let distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Give skipped zones priority by reducing their distance score
        if (isSkippedZone) {
          distance = distance * 0.8; // 20% priority boost
        }

        collisions.push({
          id: id as string,
          data: { value: distance },
        });
      }
    }

    // Sort by distance (closest first, with skipped zones prioritized)
    return collisions.sort((a, b) => a.data.value - b.data.value);
  };

  const {
    setDropIndices,
    setDragDetails,
    dropPosition,
    dropIndices,
    currentDraggedRoute,
    currentDraggedRouteLocation,
  } = useDragTooltip("route");

  const handleDragCancel = () => {
    // Reset drag state
    setIsDragging(false);
    setDropIndices(null);
    lastOverIdRef.current = null;
    dragPointerPositionRef.current = null;

    // Restore to original state before drag
    if (routesBeforeDrag.length > 0) {
      setRoutes(routesBeforeDrag);
    }
    // Note: Do NOT call resetAllSelected() - maintain selection
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Always reset drag state
    setIsDragging(false);
    setDropIndices(null);
    lastOverIdRef.current = null;
    dragPointerPositionRef.current = null;

    // Check if drop target is invalid (outside addresses column or on non-droppable area)
    const isInvalidDropTarget =
      !over ||
      (active.data.current?.type === "order" &&
        !over.data?.current?.routeId && // Not over a drop zone
        !findContainer(over.id, data)); // Not over another order

    if (isInvalidDropTarget) {
      // Drag was cancelled, reset to original state
      if (routesBeforeDrag.length > 0) {
        setRoutes(routesBeforeDrag);
      }
      return;
    }

    if (event.active.data.current?.type === "order") {
      // Pass the over data for correct sequence calculation
      changeOrderStatus(event, over);
    } else {
      changeRoutePriority(active, over);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;

    if (!over) {
      setDropIndices(null);
      lastOverIdRef.current = null;
      return;
    }

    if (active.data.current?.type === "order") {
      // Create a unique key for the current drag state to prevent redundant updates
      const currentOverKey = `${active.id}-${over.id}-${over.data?.current?.shiftType || ""}`;

      // Only process if we've moved to a different target
      if (lastOverIdRef.current === currentOverKey) {
        return; // Skip redundant updates
      }

      lastOverIdRef.current = currentOverKey;

      const newRoutes = moveOrderOnDragOver(event);

      setDragDetails(over, active, data);

      // Only update routes if they actually changed
      if (newRoutes && newRoutes !== data) {
        setRoutes(newRoutes);
      }
    }
  };

  const changeRoutePriority = (active: Active, over: Over | null) => {
    const oldIndex = data.findIndex((route) => route.id === active.id);
    const newIndex = data.findIndex((route) => route.id === over?.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const newRoutes = arrayMove(routes, oldIndex, newIndex);
      setRoutes(newRoutes);
      updateRoutePriority({
        body: {
          priority: newIndex,
        },
        pathParams: {
          routeId: active.id.toString(),
        },
      });
    }
  };

  // Divider drag handlers
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent multiple simultaneous drag operations or rapid clicks
      if (
        dragStateRef.current.isDragging ||
        dragStateRef.current.isInitializing
      ) {
        return;
      }

      if (!tableRef.current) return;

      // Mark as initializing to prevent rapid clicks
      dragStateRef.current.isInitializing = true;

      // Store the current hidden state and mouse position
      dragStateRef.current.previousHiddenColumns = new Set(hiddenColumns);
      dragStateRef.current.startX = e.clientX;

      // Temporarily show all columns to cache their boundaries
      // We need to do this in a way that doesn't cause a visible flash
      const previousHidden = hiddenColumns;
      setHiddenColumns(new Set());

      // Wait for the next frame to ensure DOM has updated with all columns visible
      requestAnimationFrame(() => {
        if (!tableRef.current || !dragStateRef.current.isInitializing) {
          // If initialization was cancelled, restore previous state
          setHiddenColumns(previousHidden);
          dragStateRef.current.isInitializing = false;
          return;
        }

        // Cache column boundaries with all columns visible
        const tableRect = tableRef.current.getBoundingClientRect();
        const headerCells = tableRef.current.querySelectorAll("thead th");
        const boundaries = new Map<string, { left: number; right: number }>();

        // Define the order of columns (right to left from Addresses)
        // Route column is excluded - it should never be hidden
        const columnOrder = [
          "totalEstimation",
          "startTime",
          "driver",
          ...(currentRouteStatus === RouteStatus.Live ||
          currentRouteStatus === RouteStatus.Planned
            ? ["notifications"]
            : []),
        ];

        headerCells.forEach((cell) => {
          const headerValue = cell.getAttribute("data-header-value");
          if (headerValue && columnOrder.includes(headerValue)) {
            const cellRect = cell.getBoundingClientRect();
            boundaries.set(headerValue, {
              left: cellRect.left - tableRect.left,
              right: cellRect.right - tableRect.left,
            });
          }
        });

        dragStateRef.current.columnBoundaries = boundaries;
        dragStateRef.current.isDragging = true;
        dragStateRef.current.isInitializing = false;
        setIsDraggingDivider(true);

        // Get the scroll container to account for scroll offset
        const scrollContainer = tableRef.current.closest(
          ".MuiTableContainer-root",
        ) as HTMLElement | null;
        const currentScrollOffset = scrollContainer?.scrollLeft || 0;

        // Immediately calculate and apply the correct hidden state based on current mouse position
        const mouseX = e.clientX - tableRect.left + currentScrollOffset;
        const newHiddenColumns = new Set<string>();

        // Sort columns by their right edge position (left to right)
        const sortedColumns = Array.from(boundaries.entries()).sort(
          (a, b) => a[1].right - b[1].right,
        );

        // Hide columns from right to left based on mouse position
        for (const [columnName, bounds] of sortedColumns) {
          const midpoint = (bounds.left + bounds.right) / 2;
          if (mouseX < midpoint) {
            newHiddenColumns.add(columnName);
          }
        }

        dragStateRef.current.lastHiddenColumns = newHiddenColumns;
        setHiddenColumns(newHiddenColumns);
        setDragDividerPosition(mouseX - 4);
      });
    },
    [currentRouteStatus, hiddenColumns],
  );

  const handleDividerMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStateRef.current.isDragging || !tableRef.current) return;

    // Cancel any pending animation frame
    if (dragStateRef.current.rafId !== null) {
      cancelAnimationFrame(dragStateRef.current.rafId);
    }

    // Use requestAnimationFrame for smooth updates
    dragStateRef.current.rafId = requestAnimationFrame(() => {
      if (!tableRef.current) return;

      // Get the scroll container to account for scroll offset
      const scrollContainer = tableRef.current.closest(
        ".MuiTableContainer-root",
      ) as HTMLElement | null;
      const currentScrollOffset = scrollContainer?.scrollLeft || 0;

      const tableRect = tableRef.current.getBoundingClientRect();
      let mouseX = e.clientX - tableRect.left + currentScrollOffset;

      // Calculate min and max boundaries from hideable columns
      const boundaries = dragStateRef.current.columnBoundaries;
      const sortedColumns = Array.from(boundaries.entries()).sort(
        (a, b) => a[1].left - b[1].left,
      );

      if (sortedColumns.length > 0) {
        const leftmostColumn = sortedColumns[0][1]; // Driver column
        const rightmostColumn = sortedColumns[sortedColumns.length - 1][1]; // Total EST column
        const minBoundary = leftmostColumn.left;
        const maxBoundary = rightmostColumn.right;

        // Clamp mouseX to stay within the hideable column boundaries
        mouseX = Math.max(minBoundary, Math.min(maxBoundary, mouseX));
      }

      // Update visual divider position to follow mouse (clamped)
      setDragDividerPosition(mouseX - 4);

      const newHiddenColumns = new Set<string>();

      // Sort columns by their right edge position (left to right)
      const sortedColumnsByRight = Array.from(
        dragStateRef.current.columnBoundaries.entries(),
      ).sort((a, b) => a[1].right - b[1].right);

      // Hide columns from right to left based on mouse position
      // Show columns one at a time when moving back right
      for (const [columnName, bounds] of sortedColumnsByRight) {
        // Column should be hidden if mouse is to the left of its midpoint
        const midpoint = (bounds.left + bounds.right) / 2;
        if (mouseX < midpoint) {
          newHiddenColumns.add(columnName);
        }
      }

      // Only update if the set actually changed
      const hasChanged =
        newHiddenColumns.size !== dragStateRef.current.lastHiddenColumns.size ||
        Array.from(newHiddenColumns).some(
          (col) => !dragStateRef.current.lastHiddenColumns.has(col),
        );

      if (hasChanged) {
        dragStateRef.current.lastHiddenColumns = newHiddenColumns;
        setHiddenColumns(newHiddenColumns);
      }
    });
  }, []);

  const handleDividerMouseUp = useCallback((e: MouseEvent) => {
    dragStateRef.current.isDragging = false;
    dragStateRef.current.isInitializing = false;
    if (dragStateRef.current.rafId !== null) {
      cancelAnimationFrame(dragStateRef.current.rafId);
      dragStateRef.current.rafId = null;
    }
    setIsDraggingDivider(false);
    setDragDividerPosition(null);

    // Check if the mouse has moved significantly (more than 5 pixels)
    const hasMovedSignificantly =
      dragStateRef.current.startX !== null &&
      Math.abs(e.clientX - dragStateRef.current.startX) > 5;

    // If the mouse hasn't moved significantly, restore the previous hidden state
    if (!hasMovedSignificantly) {
      setHiddenColumns(dragStateRef.current.previousHiddenColumns);
    }

    // Reset stored values
    dragStateRef.current.startX = null;
    dragStateRef.current.previousHiddenColumns = new Set();

    // Recalculate divider position after drag ends and layout stabilizes
    setTimeout(() => {
      if (tableRef.current) {
        const ordersHeader = tableRef.current.querySelector(
          '[data-header-value="orders"]',
        );
        if (ordersHeader) {
          const tableRect = tableRef.current.getBoundingClientRect();
          const headerRect = ordersHeader.getBoundingClientRect();
          const newPosition = headerRect.left - tableRect.left - 4;
          setDividerPosition(newPosition);
        }
      }
    }, 100);
  }, []);

  // Add/remove divider drag event listeners
  useEffect(() => {
    if (isDraggingDivider) {
      document.addEventListener("mousemove", handleDividerMouseMove);
      document.addEventListener("mouseup", handleDividerMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleDividerMouseMove);
        document.removeEventListener("mouseup", handleDividerMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
    return undefined;
  }, [isDraggingDivider, handleDividerMouseMove, handleDividerMouseUp]);

  // Calculate divider position whenever table layout changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const updateDividerPosition = () => {
      // Don't update position while dragging
      if (dragStateRef.current.isDragging) return;

      // Debounce to prevent excessive updates
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (tableRef.current && !dragStateRef.current.isDragging) {
          const ordersHeader = tableRef.current.querySelector(
            '[data-header-value="orders"]',
          );
          if (ordersHeader) {
            const tableRect = tableRef.current.getBoundingClientRect();
            const headerRect = ordersHeader.getBoundingClientRect();
            const newPosition = headerRect.left - tableRect.left - 4;

            // Always update to ensure divider appears
            setDividerPosition(newPosition);
          }
        }
      }, 50);
    };

    // Initial calculation with multiple attempts to ensure table is rendered
    const initialTimeout = setTimeout(updateDividerPosition, 100);
    const fallbackTimeout = setTimeout(updateDividerPosition, 300);
    const finalTimeout = setTimeout(updateDividerPosition, 500);

    // Recalculate on resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      if (dragStateRef.current.isDragging) return;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDividerPosition, 150);
    };

    window.addEventListener("resize", handleResize);

    // Use ResizeObserver with debounce - but skip during drag
    const observer = new ResizeObserver(() => {
      if (!dragStateRef.current.isDragging) {
        updateDividerPosition();
      }
    });

    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(fallbackTimeout);
      clearTimeout(finalTimeout);
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [hiddenColumns, data.length]);

  // Create a stable dependency for start times
  const startTimesDependency = useMemo(() => {
    return data.map((route) => route.startTime).join(",");
  }, [data]);

  const renderHourlySlots = useMemo(() => {
    // Filter out unassigned route for timetable calculations
    const realRoutesData = data.filter(
      (route) => route.id !== "unassigned-route",
    );

    // Calculate actual hours needed based on route data
    const actualTimelineSlots = generateHourlySlots(realRoutesData);
    const actualHoursNeeded = actualTimelineSlots.length;

    // Always show all hours regardless of zoom level
    // Zoom only affects the width of each hour slot, not the number of hours
    const baseHours = Math.max(24, actualHoursNeeded);

    // Generate hourly slots for all hours (zoom affects width, not count)
    const hourlySlotsData = generateZoomAwareHourlySlots(
      realRoutesData,
      baseHours,
    );

    const hourlySlots = hourlySlotsData.map((hourSlot, index) => {
      const hourWidth = TIMETABLE_ITEM_WIDTH * zoomLevel;

      // const handleMinuteClick = (e: React.MouseEvent<HTMLElement>) => {
      //   const rect = e.currentTarget.getBoundingClientRect();
      //   const clickX = e.clientX - rect.left;
      //   // Calculate which minute (1-60) based on which minute block was clicked
      //   // Each minute block is 1/60 of the hour width
      //   // Use Math.floor to determine which block, then add 1 for 1-indexed minutes
      //   const clickedMinute = Math.floor((clickX / hourWidth) * 60);
      //   const clampedMinute = Math.max(1, Math.min(60, clickedMinute));
      //   console.log(`Clicked minute: ${clampedMinute} in hour: ${hourSlot}`);
      //   // You can add your selection logic here
      // };

      return (
        <Typography
          key={`${hourSlot}-${index}`}
          variant="caption"
          fontWeight="500"
          color="#C5C6C9"
          width={hourWidth}
          className="with-minute-lines"
          // onClick={handleMinuteClick}
          sx={{
            fontSize: "11px",
            textAlign: "left",
            whiteSpace: "nowrap",
            position: "relative",
            cursor: "pointer",
            // "&::after": {
            //   content: '""',
            //   position: "absolute",
            //   inset: 0,
            //   pointerEvents: "auto",
            //   backgroundImage: `repeating-linear-gradient(
            //     to right,
            //     rgba(0, 0, 0, 0.4) 0,
            //     rgba(0, 0, 0, 0.4) 1px,
            //     transparent 1px,
            //     transparent calc(100% / 59)
            //   )`,
            // },
          }}
        >
          {hourSlot}
        </Typography>
      );
    });

    return (
      // The row already subtracts the depot icon width before rendering it.
      // Its travel/service axis starts here; shifting the header again makes
      // the clock lag every card by 12px (12 minutes at the default zoom).
      <Box sx={{ position: "relative" }}>
        <Box sx={styles.hourlySlotsContainer}>{hourlySlots}</Box>
        {(currentRouteStatus === RouteStatus.Live ||
          currentRouteStatus === RouteStatus.Planning ||
          currentRouteStatus === RouteStatus.Planned) && (
          <CurrentTimeIndicator
            timelineDate={timelineDate}
            routesMinAndMaxStartTime={minAndMaxStartTimeOfRoutes}
            totalTimelineMinutes={hourlySlotsData.length * 60}
            totalTableItems={data.length}
          />
        )}
      </Box>
    );
  }, [
    data,
    zoomLevel,
    minAndMaxStartTimeOfRoutes,
    startTimesDependency,
    timelineDate,
  ]);

  const generateHeaders = useCallback(
    ({ includeSkipped = false, useHourlySlots = false }) => {
      const allHeaders: any[] = [
        {
          label: "",
          value: "drag-handle",
          size: "3vw",
        },
        {
          label: useHourlySlots ? "" : "Route",
          value: "route",
          size: "10vw",
          minWidth: "140px",
        },
        ...(currentRouteStatus === RouteStatus.Live ||
        currentRouteStatus === RouteStatus.Planned
          ? [
              {
                label: useHourlySlots ? (
                  ""
                ) : (
                  <NotificationBell
                    variant="global"
                    globalSettings={globalNotificationSettings}
                    onSendAll={() =>
                      openModal?.(Modals.SEND_ALL_NOTIFICATIONS_GLOBAL)
                    }
                    onMuteAll={() =>
                      openModal?.(Modals.MUTE_ALL_NOTIFICATIONS_GLOBAL)
                    }
                    onCustomize={() =>
                      openModal?.(Modals.CUSTOMIZE_GLOBAL_NOTIFICATIONS)
                    }
                  />
                ),
                value: "notifications",
                size: "4vw",
                minWidth: "50px",
              },
            ]
          : []),
        {
          label: useHourlySlots ? "" : "Driver",
          value: "driver",
          size: "10vw",
          minWidth: "140px",
        },
        {
          label: useHourlySlots ? "" : "Start Time",
          value: "startTime",
          size: "7vw",
          minWidth: "100px",
        },
        {
          label: useHourlySlots ? (
            ""
          ) : currentRouteStatus === RouteStatus.Planning ? (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ whiteSpace: "nowrap" }}
            >
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                Total Time
              </Typography>
              <Tooltip title="Total Service Time">
                <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
          ) : (
            "Total EST"
          ),
          value: "totalEstimation",
          size: "5vw",
          minWidth: "100px",
        },
        {
          label: useHourlySlots ? (
            renderHourlySlots
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                lineHeight: "1.2",
                height: "auto",
              }}
            >
              Addresses
              <TimetableZoomControls />
            </Box>
          ),
          value: "orders",
          size: currentRouteStatus === RouteStatus.Live ? "unset" : "62vw",
          minWidth: "550px",
          customCSS: {
            borderLeft: "1px solid rgba(81, 81, 81, 1)",
            paddingLeft: "16px",
          },
        },
      ];

      if (includeSkipped) {
        // Calculate total skipped items across all routes
        const totalSkippedCount = data.reduce((total, route) => {
          const skippedLocations =
            route.locations?.filter((loc) => loc.order?.status === "skipped") ||
            [];
          return total + skippedLocations.length;
        }, 0);

        // Determine if content should be shown
        // Header row (useHourlySlots=false): always show content
        // Timeline row (useHourlySlots=true): show content only when scrolled
        const shouldShowContent =
          !useHourlySlots || (useHourlySlots && isHeaderHidden);

        // Determine if column should be visible (not just a placeholder)
        // Header row: always visible
        // Timeline row: visible only when scrolled OR expanded
        const shouldBeVisible =
          !useHourlySlots ||
          (useHourlySlots && (isHeaderHidden || !isSkippedCollapsed));

        allHeaders.push({
          label: shouldShowContent ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {/* Show count with Skipped text/icon based on state and scroll position */}
              {isScrolledToEnd ? (
                // When scrolled to end: show expanded text without icon
                <Typography
                  variant="caption"
                  sx={{
                    color: "#C5C6C9",
                    fontWeight: 600,
                  }}
                >
                  {totalSkippedCount} Skipped
                </Typography>
              ) : isSkippedCollapsed ? (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#FFFFFF",
                      fontWeight: 700,
                    }}
                  >
                    {totalSkippedCount}
                  </Typography>
                  <SkippedDirectionArrow color="#FFFFFF" />
                </>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#C5C6C9",
                    fontWeight: 600,
                  }}
                >
                  {totalSkippedCount} Skipped
                </Typography>
              )}
              {/* Keep the expand icon available even once scrolled to the end —
                  while collapsed, the column is still clipped to its compact
                  width there, so it's the only way to see every skipped
                  order in a route that has more than fit in that width. */}
              {hasHorizontalOverflow && (
                <IconButton
                  size="small"
                  onClick={() => setIsSkippedCollapsed(!isSkippedCollapsed)}
                  sx={{
                    padding: "2px",
                    color: isSkippedCollapsed ? "#C5C6C9" : "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {isSkippedCollapsed ? (
                    <OpenInFull sx={{ fontSize: "16px" }} />
                  ) : (
                    <CloseFullscreen sx={{ fontSize: "16px" }} />
                  )}
                </IconButton>
              )}
            </Box>
          ) : (
            ""
          ),
          value: "skipped",
          // Collapsed: fixed size so overflow:hidden actually clips to a
          // compact peek. Expanded: grows with the busiest route's skipped
          // count (see expandedSkippedColumnWidthPx above).
          size: !shouldBeVisible
            ? "0px"
            : isSkippedCollapsed
              ? `${SKIPPED_COLUMN_BASE_MIN_WIDTH}px`
              : `${expandedSkippedColumnWidthPx}px`,
          minWidth: shouldBeVisible
            ? `${SKIPPED_COLUMN_BASE_MIN_WIDTH}px`
            : "0px",
          customCSS: useHourlySlots
            ? shouldBeVisible
              ? isSkippedCollapsed
                ? {
                    borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    position: "sticky" as const,
                    right: "0",
                    zIndex: 10,
                    backgroundColor: "#1E2022",
                  }
                : {
                    position: "sticky" as const,
                    right: "0",
                    zIndex: 10,
                    backgroundColor: "#1E2022",
                    borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                  }
              : {
                  // width/minWidth here don't actually shrink the column —
                  // table-layout:auto sizes each column by its widest cell
                  // across every row, and row 1's "skipped" header always
                  // demands the real column width. So this cell renders at
                  // the full column width regardless; borderLeft: "none"
                  // used to leave a real gap in the divider line at this
                  // row instead of actually hiding anything.
                  width: "0px",
                  minWidth: "0px",
                  padding: "0px",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                  backgroundColor: "#1E2022",
                  overflow: "hidden",
                }
            : {
                position: "sticky",
                right: "0",
                zIndex: 10,
                borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                backgroundColor: "#1E2022",
              },
        });
      }

      // Filter out hidden columns
      return allHeaders.filter((header) => !hiddenColumns.has(header.value));
    },
    [
      currentRouteStatus,
      globalNotificationSettings,
      renderHourlySlots,
      hiddenColumns,
      openModal,
      isSkippedCollapsed,
      isHeaderHidden,
      isScrolledToEnd,
      hasHorizontalOverflow,
      expandedSkippedColumnWidthPx,
      data,
    ],
  );

  const sanitizedHeader = useMemo(
    () =>
      generateHeaders({
        includeSkipped: true,
        useHourlySlots: false,
      }),
    [generateHeaders, currentRouteStatus],
  );

  const secondSanitizedHeader = useMemo(
    () =>
      generateHeaders({
        includeSkipped: true,
        useHourlySlots: true,
      }),
    [generateHeaders, currentRouteStatus],
  );

  const getDriverRouteCount = (
    routeId: string,
    priorityByDriver: number | undefined = 0,
  ) => {
    const routeDriverId = routes?.find((r) => r.id === routeId)?.driver?.id;
    if (!routeDriverId) return;

    const driverRouteCount = routes.filter(
      (route) => route.driver?.id === routeDriverId,
    ).length;

    return driverRouteCount > 1
      ? `(${priorityByDriver + 1})`
      : priorityByDriver > 0
        ? priorityByDriver
        : null;
  };

  const driverOptions: ControlledSelectOption[] =
    driversData?.data?.reduce(
      (acc, driver) => {
        if (driver.id && driver.name) {
          const existingRoutes = data.filter(
            (route) => route.driver?.id === driver.id,
          );
          const existingRouteNames = existingRoutes
            .map((route) => route.region?.name ?? route.routeName)
            .join(", ");
          const driverRoute =
            existingRouteNames.length > 0 ? existingRouteNames : "No Route";
          acc.push({
            value: driver.id,
            label: (
              <Typography
                noWrap
              >{`${driver.name} (${driverRoute})`}</Typography>
            ),
            selectedLabel: driver.name,
          });
        }
        return acc;
      },
      [
        {
          label: "Any",
          value: "Any",
        },
        {
          label: "No Driver",
          value: "No Driver",
        },
      ] as ControlledSelectOption[],
    ) || [];

  // Generate hour options based on current route phase
  // In planning phase: allow all times (no restrictions)
  // In planned/live phases: restrict past times
  const shouldRestrictPastTimes = currentRouteStatus !== RouteStatus.Planning;
  const hourOptions = generateHourOptions(shouldRestrictPastTimes, 30);

  // Helper function to check if a start time dropdown should be disabled
  // Different logic for planned vs live phases
  const isStartTimeDropdownDisabled = (startTime: string): boolean => {
    // In planning phase, never disable - allow all modifications
    if (currentRouteStatus === RouteStatus.Planning) {
      return false;
    }

    if (!startTime) return false;
    const startTimeDateTime = DateTime.fromFormat(startTime, "HH:mm");
    const isCurrentlyInPast =
      getMinutesOfDay(startTimeDateTime) <= getMinutesOfDay(DateTime.local());

    // In live phase: if start time is in the past, completely disable editing
    if (currentRouteStatus === RouteStatus.Live && isCurrentlyInPast) {
      return true;
    }

    // In planned phase: never disable (user can change past times to future times)
    return false;
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminGetRoutesResDto>
  > = useMemo(() => {
    // Calculate the timeline reference for route positioning
    const realRoutesData = data.filter(
      (route) => route.id !== "unassigned-route",
    );
    const timelineReference =
      realRoutesData.length > 0
        ? getMinAndMaxTime(realRoutesData)
        : minAndMaxStartTimeOfRoutes;

    return {
      route: ({
        headerValue,
        data: { id, region, color, routeName, priorityByDriver },
        setActiveRow,
      }) => {
        const isUnassignedRoute = id === "unassigned-route";

        return {
          label: (
            <Box
              sx={styles.regionNameWrapper}
              onClick={() => {
                if (!isUnassignedRoute) {
                  onRouteClick(region?.id ?? id);
                  const index = data.findIndex(
                    ({ id: routeId }) => routeId === id,
                  );
                  setActiveRow(index);
                }
              }}
            >
              <Box
                sx={styles.regionNameColor}
                bgcolor={
                  isUnassignedRoute
                    ? palette.stone[400]
                    : paletteColorStringRenderer(
                        palette,
                        region?.color?.toLowerCase() ?? color?.toLowerCase(),
                      )
                }
              />
              <Box>
                {isUnassignedRoute ? "Unassigned" : (region?.name ?? routeName)}{" "}
                {!isUnassignedRoute &&
                  getDriverRouteCount(id, priorityByDriver)}
              </Box>
            </Box>
          ),
          value: headerValue,
        };
      },
      ...((currentRouteStatus === RouteStatus.Live ||
        currentRouteStatus === RouteStatus.Planned) && {
        notifications: ({ headerValue, data }) => {
          const isUnassignedRoute = data.id === "unassigned-route";

          return {
            label: isUnassignedRoute ? (
              <Box /> // Empty for unassigned route
            ) : (
              <NotificationBell
                variant="route"
                routeId={data.id}
                routeName={data.region?.name ?? data.routeName ?? ""}
                routeData={data}
                globalSettings={globalNotificationSettings}
                onSendAll={(routeId?: string, routeName?: string) => {
                  setPendingNotificationUpdate({
                    routeId: routeId || data.id,
                    routeName:
                      routeName || data.region?.name || data.routeName || "",
                  });
                  openModal?.(Modals.SEND_ALL_NOTIFICATIONS_ROUTE);
                }}
                onMuteAll={(routeId?: string, routeName?: string) => {
                  setPendingNotificationUpdate({
                    routeId: routeId || data.id,
                    routeName:
                      routeName || data.region?.name || data.routeName || "",
                  });
                  openModal?.(Modals.MUTE_ALL_NOTIFICATIONS_ROUTE);
                }}
                onCustomize={(routeId?: string, routeName?: string) => {
                  setPendingNotificationUpdate({
                    routeId: routeId || data.id,
                    routeName:
                      routeName || data.region?.name || data.routeName || "",
                  });
                  openModal?.(Modals.CUSTOMIZE_NOTIFICATIONS);
                }}
              />
            ),
            value: headerValue,
          };
        },
      }),
      driver: ({
        headerValue,
        data: {
          id,
          driver,
          startTime,
          availableForAnyDriver,
          region,
          routeName,
        },
      }) => {
        const isUnassignedRoute = id === "unassigned-route";

        return {
          label: isUnassignedRoute ? (
            <Box /> // Empty for unassigned route
          ) : isLoadingDrivers ? (
            <CircularProgress />
          ) : (
            <DriverControlledSelect
              driverOptions={driverOptions}
              availableForAnyDriver={availableForAnyDriver ?? false}
              driver={driver}
              handleDriverChange={(value) => {
                onSelectRouteDriver(
                  id,
                  region?.name ?? routeName ?? "",
                  startTime,
                  value,
                  driversData?.data?.find((d) => d.id === value)?.name || "",
                  data.filter((route) => route.driver?.id === value),
                );
              }}
              isDisconnected={
                currentRouteStatus === RouteStatus.Live
                  ? driverWsStatus?.data?.find(
                      (driverWs) => driverWs.driverId === driver?.id,
                    )?.status === "disconnected"
                  : false
              }
            />
          ),
          value: headerValue,
        };
      },
      startTime: ({
        headerValue,
        data: { startTime, driver, id, availableForAnyDriver },
      }) => {
        const isUnassignedRoute = id === "unassigned-route";

        return {
          // Generate a select dropdown for hours between 00:00 to 23:00
          label: isUnassignedRoute ? (
            <Box /> // Empty for unassigned route
          ) : (
            <ControlledSelect
              value={startTime || ""}
              onChange={(value) =>
                onSelectStartTime(
                  id,
                  value,
                  availableForAnyDriver ?? false,
                  driver?.id,
                )
              }
              disabled={isStartTimeDropdownDisabled(startTime)}
              onClick={(e) => {
                const isDisabled = isStartTimeDropdownDisabled(startTime);

                if (isDisabled) {
                  e.preventDefault();
                  e.stopPropagation();

                  // Different messages for different phases
                  if (currentRouteStatus === RouteStatus.Live) {
                    toast.error(
                      "Cannot modify start time for routes that have already started in live phase.",
                    );
                  } else {
                    toast.error(
                      "Cannot change start time to a past time. Please select a future time.",
                    );
                  }
                }
              }}
              options={hourOptions}
              placeholder="Select Start Time"
              sx={{
                "&.MuiSelect-filled": {
                  padding: "2px 32px 2px 8px !important",
                  minHeight: "unset !important",
                  height: "auto !important",
                },
                "& .MuiSelect-select": {
                  padding: "2px 32px 2px 8px !important",
                  minHeight: "unset !important",
                  height: "auto !important",
                },
                "& .MuiSelect-icon": {
                  right: "8px !important",
                },
              }}
            />
          ),
          value: headerValue,
        };
      },
      totalEstimation: ({ headerValue, data: route }) => {
        const { travelDetails, id } = route;
        const estimatedTimeMinutes = getRouteEstimatedTimeMinutes(
          { ...route, locations: timelineLocationsMap[id] ?? route.locations },
          currentRouteStatus === RouteStatus.Live ||
            currentRouteStatus === RouteStatus.Planned
            ? {
                serviceMinutes: allAdjustmentsMap,
                arrivalMinutes: allAdjustedEstimatedTimeMinutesMap,
              }
            : undefined,
        );
        const isUnassignedRoute = id === "unassigned-route";
        const isLivePhase = currentRouteStatus === RouteStatus.Live;

        // The API keeps the route's first full estimate next to the live one.
        const originalEstimatedTimeMinutes = (
          travelDetails as { originalEstimatedTimeMinutes?: number } | undefined
        )?.originalEstimatedTimeMinutes;
        const comparison = isLivePhase
          ? compareWithOriginalEstimate(
              estimatedTimeMinutes,
              originalEstimatedTimeMinutes,
            )
          : null;

        const estimateLabel = comparison ? (
          <Tooltip title={describeEstimateComparison(comparison)}>
            <Box display="flex" flexDirection="column" lineHeight={1.2}>
              <Box
                component="span"
                sx={{
                  color:
                    comparison.status === "behind"
                      ? palette.error.main
                      : comparison.status === "ahead"
                        ? palette.success.main
                        : undefined,
                  fontWeight:
                    comparison.status === "onSchedule" ? undefined : 600,
                }}
              >
                {formatMinutesToHoursAndMinutes(estimatedTimeMinutes)}
              </Box>
              <Box
                component="span"
                sx={{ color: palette.text.secondary, fontSize: "0.75em" }}
              >
                orig{" "}
                {formatMinutesToHoursAndMinutes(originalEstimatedTimeMinutes)}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          formatMinutesToHoursAndMinutes(estimatedTimeMinutes)
        );

        return {
          label: isUnassignedRoute ? (
            <Box /> // Empty for unassigned route
          ) : currentRouteStatus === RouteStatus.Planning ? (
            formatNumberToMinutes(travelDetails?.serviceTimeMin)
          ) : (
            estimateLabel
          ),
          value: headerValue,
        };
      },
      orders: ({
        headerValue,
        data: {
          id,
          locations,
          region,
          routeName,
          color,
          isVirtual,
          startTime,
          date,
          state,
          startingPoint,
          driver,
        },
      }) => {
        const isUnassignedRoute = id === "unassigned-route";

        return {
          label: (
            <OrderLayout
              locations={timelineLocationsMap[id] ?? locations ?? []}
              routeId={id}
              isVirtualRoute={isVirtual}
              color={
                isUnassignedRoute
                  ? "stone"
                  : (region?.color?.toLowerCase() ?? color?.toLowerCase() ?? "")
              }
              onClickedAddress={(clickedAddress: ClickedAddress) =>
                onClickedAddress({
                  e: clickedAddress.e,
                  address: clickedAddress.address,
                })
              }
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              onDeleteClick={(routeId) =>
                handleDeleteVirtualRoute(
                  routeId,
                  region?.name ?? routeName ?? "",
                )
              }
              currentRouteStatus={currentRouteStatus}
              routeStartTime={startTime}
              routeDate={rowTimelineDate({ date, state, locations })}
              startingPoint={startingPoint}
              driverFullName={driver?.name}
              isUnassignedRoute={isUnassignedRoute}
              timelineReference={timelineReference}
              skippedPortalTarget={skippedCellTargets[id]}
              isDraggingGlobal={isDragging}
              onAdjustmentsChange={handleAdjustmentsChange}
            />
          ),
          value: headerValue,
          customCSS: {
            borderLeft: "1px solid rgba(81, 81, 81, 1)",
            paddingLeft: "16px",
            position: "relative" as const,
          },
        };
      },
      skipped: ({ headerValue, data: { id } }) => {
        return {
          // Always mounted so OrderLayout has a stable DOM node to portal
          // its skipped-orders content into.
          label: (
            <Box
              ref={getSkippedCellRefCallback(id)}
              sx={{ width: "100%", height: "100%" }}
            />
          ),
          value: headerValue,
          // Collapsed: NOT sticky — it's just a normal, compact (150px),
          // clipped column like any other, so it appears exactly where it
          // naturally sits in the row as the user scrolls right, with no
          // artificial hide/reveal toggling (no "popping in", no gap-zone
          // where unclipped content could overlap the Time/Addresses area —
          // overflow:hidden bounds it to its own cell at every scroll
          // position). Expanded: sticky + full width so every skipped order
          // stays visible regardless of scroll position — that's the whole
          // point of expanding.
          customCSS: isSkippedCollapsed
            ? {
                backgroundColor: "#1E2022",
                borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                overflow: "hidden",
              }
            : {
                position: "sticky",
                right: "0",
                zIndex: 1,
                backgroundColor: "#1E2022",
                borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
              },
        };
      },
    };
  }, [
    driverOptions,
    currentRouteStatus,
    data,
    timelineLocationsMap,
    rowTimelineDate,
    minAndMaxStartTimeOfRoutes,
    startTimesDependency,
    isSkippedCollapsed,
    isDragging,
    skippedCellTargets,
    getSkippedCellRefCallback,
    allAdjustmentsMap,
    allAdjustedEstimatedTimeMinutesMap,
  ]);

  const totalItems = useMemo(() => {
    return currentDraggedRoute && currentDraggedRouteLocation
      ? (
          currentDraggedRoute.locations?.filter(
            (loc) =>
              loc.order?.status?.toLowerCase() ===
              currentDraggedRouteLocation.order?.status?.toLowerCase(),
          ) || []
        ).length
      : (currentDraggedRoute?.locations?.length ?? 0);
  }, [currentDraggedRoute, currentDraggedRouteLocation]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
    >
      <Box
        ref={tableRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          isolation: "isolate",
        }}
      >
        <GenericTable
          data={data}
          sx={styles.routePlannerTableStyle}
          headers={sanitizedHeader}
          secondHeaders={secondSanitizedHeader}
          renderRowLabel={renderRowLabel}
          isHeaderSticky={true}
          sortOptions={sortOptions}
          onSortChange={sortOptionsChange}
          isLoading={isLoading}
          draggable
          shouldAddOnHover={currentRouteStatus !== RouteStatus.Planning}
          onAddClick={handleAddVirtualRoute}
          compactCellPadding
        />
        {/* Draggable divider on the border of Addresses column */}
        {dividerPosition !== null && (
          <Box
            onMouseDown={handleDividerMouseDown}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              top: "20px",
              bottom: "8px",
              width: "12px",
              left: `${isDraggingDivider && dragDividerPosition !== null ? dragDividerPosition - 2 : dividerPosition - 2}px`,
              cursor: "col-resize",
              zIndex: 10000,
              pointerEvents: "auto",
              display: "flex",
              alignItems: "stretch",
              justifyContent: "center",
              overflow: "hidden",
              transition: isDraggingDivider
                ? "none"
                : "left 0.2s ease-out, background-color 0.15s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
              },
              "&:hover::after": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                width: "2px",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
              },
              "&:active": {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
              },
              ...(isDraggingDivider && {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "2px",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                },
              }),
            }}
          />
        )}
        {/* Overlay to prevent row hover during drag */}
        {isDraggingDivider && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              cursor: "col-resize",
              userSelect: "none",
            }}
          />
        )}
      </Box>
      <HoveredAddress
        hoveredAddress={hoveredAddress}
        isHovering={isHovering}
        handleBoxMouseEnter={handleBoxMouseEnter}
        handleBoxMouseLeave={handleBoxMouseLeave}
        hoverPosition={hoverPosition}
        fadeRef={fadeRef}
        adjustedServiceTimeMap={allAdjustmentsMap}
        adjustedEstimatedTimeMinutesMap={allAdjustedEstimatedTimeMinutesMap}
        estimatedArrivalTimeMap={estimatedArrivalTimeMap}
        serviceTimeMap={serviceTimeMap}
        currentRouteStatus={currentRouteStatus}
      />
      <DropPositionTooltip
        isVisible={isDragging && dropIndices !== null}
        position={dropPosition}
        beforeIndex={dropIndices?.before ?? 0}
        afterIndex={dropIndices?.after ?? 0}
        totalItems={totalItems}
        regionName={currentDraggedRoute?.routeName ?? ""}
        regionColor={
          currentDraggedRoute?.color || currentDraggedRoute?.region?.color || ""
        }
        serviceTime={
          currentDraggedRouteLocation?.serviceTime
            ? currentRouteStatus === RouteStatus.Live
              ? formatNumberToMinutesPrecise(
                  currentDraggedRouteLocation.serviceTime,
                )
              : formatNumberToMinutes(currentDraggedRouteLocation.serviceTime)
            : ""
        }
        orderDetails={currentDraggedRouteLocation?.order}
      />
    </DndContext>
  );
};

const useStyles = makeSxStyles(() => ({
  routePlannerTableStyle: {
    mt: "6px",
    flex: "1 1 75px",
    maxHeight: "fit-content",
    borderRadius: "8px !important",
    overflow: "auto",
    "& table": {
      paddingBottom: "8px",
      borderRadius: "8px",
    },
    "& thead tr:first-of-type th:first-of-type": {
      borderTopLeftRadius: "8px",
    },
    "& thead tr:first-of-type th:last-of-type": {
      borderTopRightRadius: "8px",
    },
    "& thead tr:first-of-type th[data-header-value='skipped']": {
      borderTopRightRadius: "0px",
    },
    "& tbody tr:last-of-type td:first-of-type": {
      borderBottomLeftRadius: "8px",
    },
    "& tbody tr:last-of-type td:last-of-type": {
      borderBottomRightRadius: "8px",
    },
    "& tbody tr:last-of-type td[data-header-value='skipped']": {
      borderBottomRightRadius: "0px",
    },
    "& tbody tr": {
      background: "#1A1C1E",
    },
    "& thead th": {
      // Completed stop cards rise to z-index 5 on hover; keep them below the header.
      zIndex: 10,
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
    "& thead tr:last-of-type th": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
    "& tbody td": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
    },
    // The skipped column is the table's last column — its usual trailing
    // padding shows up as an extra grey gutter after the widest route's
    // chips once you scroll all the way to the right, on top of the column
    // already being sized to fit that row exactly.
    // Every body cell (including the drag-handle) renders as an actual
    // <th> via MUI's component="th" prop, not a <td> — the "& tbody td"
    // rule above never matches anything in the body for that reason, and
    // body cells don't carry a data-header-value attribute either (only
    // <thead> cells do — see generic-table.tsx). Target by position
    // instead: "skipped" is always the last cell in each row.
    "& tbody th:last-of-type": {
      paddingRight: "0px !important",
    },
  },
  regionNameWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
  },
  orderTimeTables: {
    display: "flex",
    paddingLeft: "16px",
    alignItems: "center",
  },
  regionNameColor: {
    display: "inline-flex",
    width: "6px",
    height: "14px",
    borderRadius: "2px",
    marginRight: "4px",
  },
  hourlySlotsContainer: {
    display: "flex",
    alignItems: "center",
  },
}));

export default RoutePlannerTable;
